import React from 'react';

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h2 style={{
        fontFamily: "'Oswald', sans-serif",
        fontSize: '15px',
        fontWeight: 600,
        color: '#001a4d',
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        marginBottom: '10px',
        marginTop: '28px',
      }}>{title}</h2>
      {children}
    </div>
  );
}

export default function PrivacyNoticeContent({ requiredPrivacyVersion = '2026-05-13' }) {
  return (
    <>
      <h1 style={{
        fontFamily: "'Oswald', sans-serif",
        fontSize: '24px',
        fontWeight: 700,
        color: '#001a4d',
        letterSpacing: '1px',
        marginTop: 0,
        marginBottom: '8px',
      }}>VCIN Privacy Notice</h1>

      <p style={{ fontSize: '13px', color: '#6b7a94', marginBottom: '28px' }}>
        <strong>Version {requiredPrivacyVersion}</strong>
      </p>

      <Section title="Who runs VCIN">
        <p>VCIN (Vehicle Crime Intelligence Network) is operated by <strong>Digital Software Group Ltd</strong>, registered in England and Wales. We are the data controller for the information described below.</p>
        <p>You can contact us at: <strong>privacy@vcin.co.uk</strong></p>
      </Section>

      <Section title="What VCIN is for">
        <p>VCIN is a closed, vetted intelligence-sharing platform for UK security professionals. Officers in the private security industry use it to record sightings of vehicles linked to suspected criminal activity, and to check whether a vehicle has been seen before by other officers on the network.</p>
      </Section>

      <Section title="What data we process about you">
        <p>When you use VCIN as a vetted user, we process the following information about you:</p>
        <ul>
          <li><strong>Your identity:</strong> your name, SIA licence number, the company you work for, the sites you're assigned to, and your mobile number (used for two-factor authentication)</li>
          <li><strong>Your account activity:</strong> when you log in, what searches you run, what sightings you submit, what threat intelligence you view</li>
          <li><strong>Your sighting submissions:</strong> the narratives, locations, and categorisations you record when reporting a vehicle</li>
        </ul>
        <p>We process this information so that VCIN can verify you are who you say you are, record your contributions to the platform's intelligence picture, and hold individuals accountable for the information they record under their name.</p>
      </Section>

      <Section title="What we do with vehicle and incident data">
        <p>When you submit a sighting, VCIN records the vehicle's registration number, your description of the incident, the category (such as robbery, theft, fly-tipping), and the date and location. This information is shared with other vetted users of the platform who later check the same vehicle.</p>
        <p>VCIN processes information that relates to <strong>criminal offences and alleged criminal conduct</strong>. This is "criminal offence data" under UK GDPR Article 10 and the Data Protection Act 2018. Our lawful basis for processing it is:</p>
        <ul>
          <li><strong>Article 6(1)(f)</strong> — our legitimate interest in preventing and detecting crime affecting the UK security industry and the sites it protects</li>
          <li><strong>Schedule 1 paragraph 10 of the Data Protection Act 2018</strong> — the substantial public interest condition of preventing or detecting unlawful acts</li>
        </ul>
        <p>We have an Appropriate Policy Document explaining how we comply with the safeguards required by Schedule 1. You can request a copy at the contact details above.</p>
      </Section>

      <Section title="How long we keep data">
        <p>We don't keep data forever. Different types of information have different retention periods:</p>
        <ul>
          <li><strong>Your account profile:</strong> retained while your account is active</li>
          <li><strong>Sighting narratives, locations, and identifying details:</strong> redacted or pseudonymised after 6 years</li>
          <li><strong>Aggregate vehicle records and historic counts:</strong> retained indefinitely as intelligence record</li>
          <li><strong>Audit logs of your platform activity:</strong> retained for 7 years, then audit log entries about you are pseudonymised</li>
        </ul>
        <p>A full retention schedule is available on request.</p>
      </Section>

      <Section title="Who else sees this data">
        <ul>
          <li>Other vetted users who check the same vehicle you've reported on will see the threat level and region of your sighting, but <strong>not</strong> your name or the narrative you wrote</li>
          <li>Your own company's administrator can see your sighting submissions and account activity</li>
          <li>Our Super Administrators can see all data on the platform — they are the operators of VCIN</li>
          <li>We may share information with the police or other competent authorities where lawfully required to do so</li>
          <li>We use sub-processors to host the platform (Supabase, in Ireland, for the database; Render, in Frankfurt, for the application; Clerk for authentication; SendGrid for email)</li>
        </ul>
        <p>We do <strong>not</strong> sell your data, share it with marketers, or use it for advertising.</p>
      </Section>

      <Section title="Your rights under UK GDPR">
        <p>You have the right to:</p>
        <ul>
          <li><strong>Be informed</strong> about how we process your data (this notice)</li>
          <li><strong>Request access</strong> to the data we hold about you</li>
          <li><strong>Request rectification</strong> of inaccurate information</li>
          <li><strong>Request restriction</strong> of processing in certain circumstances</li>
          <li><strong>Object</strong> to processing in certain circumstances</li>
          <li><strong>Request erasure</strong> (the "right to be forgotten") — subject to our legitimate interest in retaining intelligence-related records for the periods stated above</li>
          <li><strong>Withdraw consent</strong> at any time, though most processing on VCIN is not based on consent</li>
          <li><strong>Lodge a complaint</strong> with the Information Commissioner's Office at <strong>ico.org.uk</strong> if you believe we have processed your data unlawfully</li>
        </ul>
        <p>To exercise any of these rights, contact us at the email address above.</p>
      </Section>

      <Section title="Automated decision-making">
        <p>VCIN calculates a "threat level" for each vehicle based on the number and category of sightings logged against it. This is statistical pattern-recognition, not an automated decision about any individual. Threat level information is contextual support for human decisions — it is never used to deny anyone a right, restrict their movement, or make any consequential determination automatically.</p>
      </Section>

      <Section title="How this notice may change">
        <p>The platform's scope is expected to expand in future to include other UK vehicle-crime stakeholders (local authority enforcement teams, the Environment Agency, agricultural sector networks, and Police and Crime Commissioner oversight). When the scope of processing materially changes, this notice will be updated and you will be asked to review and accept the new version before continuing to use VCIN.</p>
      </Section>
    </>
  );
}
